<script setup>
import { h, onMounted, reactive, ref, unref } from "vue";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { deleteRequest, getRequest, postRequest } from "@/utils/request.js";
import ComplaintModal from "@/views/complaint/modules/ComplaintModal.vue";
import { message } from "ant-design-vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();

const columns = [
  {
    key: "realName",
    dataIndex: "realName",
    title: "举报人",
    width: "5%",
  },
  {
    key: "typeName",
    dataIndex: "typeName",
    title: "举报原因",
    width: "5%",
  },
  {
    key: "targetType",
    dataIndex: "targetType",
    title: "举报对象",
    width: "5%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "状态",
    width: "5%",
  },
  {
    key: "appliedTime",
    dataIndex: "appliedTime",
    title: "举报时间",
    width: "8%",
  },
  {
    key: "acceptedTime",
    dataIndex: "acceptedTime",
    title: "受理时间",
    width: "8%",
  },
  {
    key: "rejectTime",
    dataIndex: "rejectTime",
    title: "驳回时间",
    width: "8%",
  },
  {
    key: "rejectReason",
    dataIndex: "rejectReason",
    title: "驳回原因",
    width: "8%",
  },
  {
    key: "completeTime",
    dataIndex: "completeTime",
    title: "处理时间",
    width: "8%",
  },
  {
    key: "completeContent",
    dataIndex: "completeContent",
    title: "处理结果",
    width: "8%",
  },
  {
    key: "returnVisitTime",
    dataIndex: "returnVisitTime",
    title: "回访时间",
    width: "8%",
  },
  {
    key: "returnVisitContent",
    dataIndex: "returnVisitContent",
    title: "回访记录",
    width: "8%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "100px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;

  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });

  getRequest("/complaint", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleDetail = (record) => {
  modalRef.value.handleShow(record);
};

const handleApplied = (record) => {
  postRequest("/complaint/accepted", record).then((response) => {
    if (response.success) {
      message.success("受理成功");
      loadData();
    } else {
      message.error("受理失败," + response.message);
    }
  });
};

const handleComplete = (record) => {
  modalRef.value.handleComplete(record);
};

const handleReturnVisit = (record) => {
  modalRef.value.handleReturnVisit(record);
};

const handleReject = (record) => {
  modalRef.value.handleReject(record);
};

const handleRemove = (record) => {
  deleteRequest(`/favorite/${record.id}`).then((response) => {
    if (response.success) {
      loadData();
    }
  });
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 8, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="公司/个人名称" name="name">
              <a-input
                v-model:value.trim="searchFormModel.name"
                placeholder="请输入公司/个人名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="统一社会信用代码/身份证号" name="code">
              <a-input
                v-model:value.trim="searchFormModel.code"
                placeholder="请输入统一社会信用代码/身份证号"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'realName'">
            <template v-if="record['userType'] === 'JOB_SEEKER'">
              <a-tag color="blue"> 求职者</a-tag>
            </template>
            <template v-if="record['userType'] === 'RECRUITER'">
              <a-tag color="green"> 招聘者</a-tag>
            </template>
            {{ record[column.dataIndex] || "未完善信息用户" }}
          </template>

          <template v-if="column.dataIndex === 'targetType'">
            <template v-if="record[column.dataIndex] === 'PEOPLE'">
              <a-tag color="green"> 招聘者/求职者</a-tag>
            </template>
            <template v-if="record[column.dataIndex] === 'PLATFORM'">
              <a-tag color="blue"> 平台</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex] === 'APPLIED'">
              已申请
            </template>
            <template v-if="record[column.dataIndex] === 'ACCEPTED'">
              已受理
            </template>
            <template v-if="record[column.dataIndex] === 'REJECT'">
              驳回
            </template>
            <template v-if="record[column.dataIndex] === 'COMPLETE'">
              已处理
            </template>
            <template v-if="record[column.dataIndex] === 'RETURN_VISIT'">
              已回访
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handleDetail(record)">查看</a>
            <template v-if="record['status'] === 'APPLIED'">
              <a-divider type="vertical" />
              <a-popconfirm
                title="确认要受理吗?"
                @confirm="handleApplied(record)"
              >
                <a type="link">受理</a>
              </a-popconfirm>
              <a-divider type="vertical" />
              <a type="link" @click="handleReject(record)">驳回</a>
            </template>
            <template v-if="record['status'] === 'ACCEPTED'">
              <a-divider type="vertical" />
              <a type="link" @click="handleComplete(record)">处理</a>
            </template>
            <template v-if="record['status'] === 'COMPLETE'">
              <a-divider type="vertical" />
              <a type="link" @click="handleReturnVisit(record)">回访</a>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
    <complaint-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
